<script lang="ts" setup>
import { useMateTypeText } from "@/use/common";
import { getDisplayDateTime } from "@/modules/common";
import { CoinBalanceRecordEntityType, CoinVote } from "@/types/coin";
import { useRouter } from "vue-router";
import md5 from "crypto-js/md5";
import { NORMAL_TICKETS_PER_GOLD_TICKET } from "@/stores/coin";

const props = defineProps<{
  coinVote: CoinVote;
}>();

const router = useRouter();

const goMateHome = (uuid: string) => {
  router.push({ name: "mateHome", params: { uuid } });
};
</script>
<template>
  <div class="flex flex-col gap-2 bg-neutral p-2">
    <div class="flex">
      <div
        :class="coinVote.isAnonymous ? 'cursor-default' : 'cursor-pointer'"
        class="flex gap-2"
        @click="!coinVote.isAnonymous && goMateHome(coinVote.mate!.uuid)"
      >
        <user-avatar
          v-if="!coinVote.isAnonymous"
          :avatar-url="coinVote.mate?.mediaUrl"
          class="w-8"
        />
        <div
          v-else
          class="w-8 bg-primary rounded flex items-center justify-center aspect-[3/4] px-1"
        >
          <i class="iconfont icon-cut-face text-base-content/50" />
        </div>
        <div class="flex flex-col justify-between flex-1">
          <div class="line-clamp-1">
            <template v-if="coinVote.isAnonymous">
              {{ $t("Coin.anonymousUser") }}-<span
                class="text-base-content/50"
                >{{
                  md5(coinVote.userId.toString()).toString().slice(0, 8)
                }}</span
              >
            </template>
            <template v-else>
              {{ coinVote.mate?.name }}
            </template>
          </div>
          <span v-if="!coinVote.isAnonymous" class="flex items-center">
            <span class="text-xs text-base-content/50">
              {{ useMateTypeText(coinVote.mate!.type) }}
            </span>
            <gender-badge :gender="coinVote.mate?.gender" class="w-4" />
          </span>
          <span v-else class="text-xs text-base-content/50">
            {{ $t("Coin.anonymousVote") }}
          </span>
        </div>
      </div>
      <div class="flex flex-col justify-between items-end flex-grow shrink-0">
        <div class="flex items-center gap-1">
          <div
            v-if="coinVote.isGolden === 1"
            class="text-my-yellow flex items-center gap-1"
          >
            +1
            <i class="iconify mdi--ticket text-sm text-my-yellow" />
          </div>
          <div class="text-primary flex items-center gap-1">
            <template v-if="coinVote.isGolden === 1">
              (+{{ coinVote.amount }} <i class="iconify mdi--ticket text-sm" />)
            </template>
            <template v-else>
              +{{ coinVote.amount }}
              <i class="iconify mdi--ticket text-sm" />
            </template>
          </div>
        </div>
        <div class="text-xs text-base-content/50">
          {{ getDisplayDateTime(coinVote.dtCreate) }}
        </div>
      </div>
    </div>
    <div class="whitespace-pre-line">
      {{ coinVote.text }}
    </div>
    <div class="bg-primary p-2 rounded cursor-pointer">
      <div
        v-if="coinVote.entityType === CoinBalanceRecordEntityType.Mate"
        @click="
          router.push({
            name: 'mateHome',
            params: { uuid: coinVote.entity.uuid },
          })
        "
      >
        <mate-vote-card :mate="coinVote.entity as Mate" />
      </div>
      <div
        v-if="
          coinVote.entityType === CoinBalanceRecordEntityType.StoryBook ||
          coinVote.entityType === CoinBalanceRecordEntityType.Story
        "
        @click="
          router.push({
            name:
              coinVote.entityType === CoinBalanceRecordEntityType.StoryBook
                ? 'storyBookHome'
                : 'readStory',
            params: { uuid: coinVote.entity.uuid },
          })
        "
      >
        <story-vote-card :story-book="coinVote.entity as StoryBook" />
      </div>
      <div
        v-if="coinVote.entityType === CoinBalanceRecordEntityType.Game"
        @click="
          router.push({
            name: 'galGameDetail',
            params: { uuid: coinVote.entity.uuid },
          })
        "
      >
        <game-vote-card :game="coinVote.entity as GalGame" />
      </div>
      <div
        v-if="coinVote.entityType === CoinBalanceRecordEntityType.Collection"
        @click="
          router.push({
            name: 'collectionMain',
            params: { uuid: coinVote.entity.uuid },
          })
        "
      >
        <CollectionVoteCard :collection="coinVote.entity as Collection" />
      </div>
    </div>
  </div>
</template>
